import React from 'react';
import {Ban, AlertCircle} from 'lucide-react';
import {Alert, AlertDescription} from '@/components/ui/alert';
import {Card, CardContent, CardHeader} from '@/components/ui/card';
import {Button} from '@/components/ui/button';
import {WorkflowLogStatus, WorkflowRunListObject} from "@/types/workflowRuns.js";
import homePaths from "@/constants/homePaths.js";
import {useNavigate} from "react-router-dom";
import NeedHelpAlert from "@/components/TaskHub/NeedHelpAlert.js";

type TaskCancelledViewProps = {
  application: WorkflowRunListObject;
};

const ApplicationCancelledView = (props: TaskCancelledViewProps) => {
  const {application} = props;
  const nav = useNavigate();

  const cancelledLog = application.workflow_log?.workflow_log_records.find(row => row.log_record_status === WorkflowLogStatus.WORKFLOW_CANCELLED);
  const cancelledTimestamp = cancelledLog?.log_record_timestamp;

  const formatTimestamp = (timestamp: string | undefined) => {
    if (!timestamp) {return '';}
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'long',
      timeStyle: 'short'
    }).format(date);
  }

  const goNewApplicationLink = () => {
    nav([homePaths.home, homePaths.new].join(''), {replace: true});
  }

  return (
    <div className="max-w-2xl mx-auto p-9 md:p-16">

      <Card className='bg-white'>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Ban className="h-5 w-5 text-red-500"/>
                <h1 className="text-2xl font-semibold text-gray-900">Application Cancelled</h1>
              </div>
              <p className="text-gray-600">
                {cancelledTimestamp
                  ? `This application was cancelled on ${formatTimestamp(cancelledTimestamp)}.`
                  : 'This application has been cancelled.'}
              </p>
            </div>
            <span className="px-4 py-1 bg-red-50 text-red-700 rounded-md font-medium">
              Cancelled
            </span>
          </div>
        </CardHeader>
        <CardContent>

          <Alert className="mb-6" variant="destructive">
            <AlertCircle className="h-4 w-4"/>
            <AlertDescription>
              This application has been cancelled and is no longer being processed.
              If you need to submit a new application, please start a new request.
            </AlertDescription>
          </Alert>

          <div className="mt-6 flex gap-4">
            <Button
              className="w-full sm:w-auto"
              onClick={() => goNewApplicationLink()}
              variant="kay"
            >
              Start New Application
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="mt-6">
        <NeedHelpAlert />
      </div>
    </div>
  );
};

export default ApplicationCancelledView;
